import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { canRevertFinancialDocumentRename } from "@common/helpers/financialDocument";
import { FinancialDocumentBulkActionTypes } from "@common/constants";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { RemovingDocumentWithTransactionsErrorSchema } from "@common/schemas/financialDocument/removing-document-with-transactions-error.schema";
import { RemoveAction } from "@common/types/financialDocument";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";
import { useFinancialDocumentBulkSelection } from "./FinancialDocumentBulkSelection/useFinancialDocumentBulkSelection";
import { useFinancialDocumentBulkActions } from "./FinancialDocumentBulkActions/FinancialDocumentBulkActions";
import { getDocumentLockedTooltip, isDocumentFieldLocked } from "./lib";
import { useDocumentCommandOptions } from "./useDocumentCommandOptions";
import { useFinancialDocumentsBulkCreateTask } from "./useFinancialDocumentsBulkCreateTask";

interface UseDocumentBulkActionsParams {
    /** More rows match than the table holds; "Select all" shows while true. */
    hasUnloadedRows: boolean;
}

export function useDocumentBulkActions({
    hasUnloadedRows,
}: UseDocumentBulkActionsParams) {
    const { hasAccess: hasImporterAccess } = useAllowFeature(
        PREMIUM_FEATURES.IMPORTER,
    );
    const bulkSelection = useFinancialDocumentBulkSelection();
    const bulkActions = useFinancialDocumentBulkActions();
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] =
        useState(false);
    const [isUnmatchConfirmationOpen, setIsUnmatchConfirmationOpen] =
        useState(false);
    const [transactionsToRemoveCount, setTransactionsToRemoveCount] =
        useState<number>();

    const lockedSelectionTooltip = useMemo(() => {
        if (bulkSelection.filtersSelection) {
            return undefined;
        }
        const lockedDoc = bulkSelection.selected.find((doc) =>
            isDocumentFieldLocked(doc, doc.integrationAccount ?? null),
        );
        if (!lockedDoc) {
            return undefined;
        }
        return getDocumentLockedTooltip(
            lockedDoc,
            lockedDoc.integrationAccount ?? null,
        );
    }, [bulkSelection.selected, bulkSelection.filtersSelection]);

    const matchedDocumentsCount = useMemo(() => {
        if (bulkSelection.filtersSelection) {
            return bulkSelection.selectedCount;
        }
        return bulkSelection.selected.filter(
            (doc) => doc.transactionMatches.length > 0,
        ).length;
    }, [
        bulkSelection.selected,
        bulkSelection.selectedCount,
        bulkSelection.filtersSelection,
    ]);

    const reconciledCount = useMemo(() => {
        if (bulkSelection.filtersSelection) {
            return 0;
        }
        return bulkSelection.selected.filter((doc) => doc.isReconciled).length;
    }, [bulkSelection.selected, bulkSelection.filtersSelection]);

    const isAllReconciled =
        reconciledCount > 0 && reconciledCount === bulkSelection.selectedCount;
    const removableCount = bulkSelection.selectedCount - reconciledCount;

    const confirmRemoveButtonLabel = isAllReconciled
        ? "OK"
        : "Remove documents";

    const buildRemoveAction = useCallback(
        (
            options: { confirmTransactionsRemoval?: boolean } = {},
        ): RemoveAction => ({
            type: FinancialDocumentBulkActionTypes.REMOVE,
            ...(options.confirmTransactionsRemoval
                ? { confirmTransactionsRemoval: true }
                : {}),
            ...(reconciledCount > 0 || bulkSelection.filtersSelection
                ? { skipReconciledDocuments: true }
                : {}),
        }),
        [reconciledCount, bulkSelection.filtersSelection],
    );

    const handleRemove = useCallback(async () => {
        try {
            await bulkActions.run(buildRemoveAction());
            bulkSelection.clearSelection();
        } catch (error) {
            const parsed = isAxiosError(error)
                ? RemovingDocumentWithTransactionsErrorSchema.safeParse(
                      error.response?.data,
                  )
                : undefined;
            if (parsed?.success) {
                setTransactionsToRemoveCount(
                    parsed.data.documents.reduce(
                        (count, doc) => count + doc.transactionsCount,
                        0,
                    ),
                );
            }
            // Other errors are handled by the BulkActionsProvider's onError callback
        }
        setIsRemoveConfirmationOpen(false);
    }, [bulkActions, bulkSelection, buildRemoveAction]);

    const handleConfirmTransactionsRemoval = useCallback(async () => {
        try {
            await bulkActions.run(
                buildRemoveAction({ confirmTransactionsRemoval: true }),
            );
            bulkSelection.clearSelection();
        } catch {
            // Error handling is done by the BulkActionsProvider's onError callback
        }
        setTransactionsToRemoveCount(undefined);
    }, [bulkActions, bulkSelection, buildRemoveAction]);

    const handleUnmatch = useCallback(async () => {
        try {
            await bulkActions.run({
                type: FinancialDocumentBulkActionTypes.UNMATCH_TRANSACTIONS,
            });
            bulkSelection.clearSelection();
        } catch {
            // Error handling is done by the BulkActionsProvider's onError callback
        }
        setIsUnmatchConfirmationOpen(false);
    }, [bulkActions, bulkSelection]);

    const hasRenamedDocuments = useMemo(() => {
        if (bulkSelection.filtersSelection) {
            return true;
        }
        return bulkSelection.selected.some((doc) =>
            canRevertFinancialDocumentRename({
                fileName: doc.fileName,
                originalFileName: doc.originalFileName,
            }),
        );
    }, [bulkSelection.selected, bulkSelection.filtersSelection]);

    const handleRevertRename = useCallback(async () => {
        closeCommands();
        try {
            await bulkActions.run({
                type: FinancialDocumentBulkActionTypes.REVERT_RENAME,
            });
            bulkSelection.clearSelection();
        } catch {
            // Error handling is done by the BulkActionsProvider's onError callback
        }
    }, [bulkActions, bulkSelection, closeCommands]);

    const handleCreateFinancialDocumentsTask =
        useFinancialDocumentsBulkCreateTask();

    const { entityOptions, accountOptions, categoryOptions } =
        useDocumentCommandOptions({
            isFieldLocked: !!lockedSelectionTooltip,
        });

    const handleSetEntityClick = useCallback(() => {
        if (!bulkSelection.selectedCount) {
            return;
        }
        openCommands(CommandsDirectory.DOCUMENT_BULK_ENTITY, {
            documentBulkEntityOptions: entityOptions,
            selectedDocumentCount: bulkSelection.selectedCount,
        });
    }, [openCommands, bulkSelection.selectedCount, entityOptions]);

    const handleSetAccountClick = useCallback(() => {
        if (!bulkSelection.selectedCount) {
            return;
        }
        openCommands(CommandsDirectory.DOCUMENT_BULK_ACCOUNT, {
            documentBulkAccountOptions: accountOptions,
            selectedDocumentCount: bulkSelection.selectedCount,
        });
    }, [openCommands, bulkSelection.selectedCount, accountOptions]);

    const handleOpenBulkActions = useCallback(() => {
        openCommands(CommandsDirectory.DOCUMENT_BULK, {
            documentBulkEntityOptions: entityOptions,
            documentBulkAccountOptions: accountOptions,
            documentBulkCategoryOptions: categoryOptions,
            isDocumentBulkFieldLocked: !!lockedSelectionTooltip,
            documentBulkFieldLockedTooltip: lockedSelectionTooltip,
            onDocumentBulkRemove: () => setIsRemoveConfirmationOpen(true),
            onDocumentBulkUnmatch:
                matchedDocumentsCount > 0
                    ? () => setIsUnmatchConfirmationOpen(true)
                    : undefined,
            onDocumentBulkAddToImporter: hasImporterAccess
                ? () =>
                      bulkActions.run({
                          type: FinancialDocumentBulkActionTypes.ADD_TO_IMPORTER,
                      })
                : undefined,
            onDocumentBulkCreateTask: handleCreateFinancialDocumentsTask,
            onDocumentBulkRevertRename: hasRenamedDocuments
                ? handleRevertRename
                : undefined,
            selectedDocumentCount: bulkSelection.selectedCount,
        });
    }, [
        openCommands,
        entityOptions,
        accountOptions,
        categoryOptions,
        lockedSelectionTooltip,
        matchedDocumentsCount,
        bulkActions,
        bulkSelection.selectedCount,
        handleCreateFinancialDocumentsTask,
        hasImporterAccess,
        hasRenamedDocuments,
        handleRevertRename,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (bulkSelection.selectedCount > 0) {
            registerOpenOverride(() => handleOpenBulkActionsRef.current());
            return () => unregisterOpenOverride();
        }
        unregisterOpenOverride();
    }, [
        bulkSelection.selectedCount,
        registerOpenOverride,
        unregisterOpenOverride,
    ]);

    return {
        bulkSelection,
        bulkActions,
        isRemoveConfirmationOpen,
        setIsRemoveConfirmationOpen,
        isUnmatchConfirmationOpen,
        setIsUnmatchConfirmationOpen,
        isAllReconciled,
        removableCount,
        matchedDocumentsCount,
        reconciledCount,
        confirmRemoveButtonLabel,
        transactionsToRemoveCount,
        setTransactionsToRemoveCount,
        handleRemove,
        handleConfirmTransactionsRemoval,
        handleUnmatch,
        handleSetEntityClick,
        handleSetAccountClick,
        handleOpenBulkActions,
        lockedSelectionTooltip,
        showSelectAll:
            !bulkSelection.hasSelectedByCurrentFilters && hasUnloadedRows,
    };
}
